/**
 * /check-in/new — staff page to start a new Check-In Session.
 * Spec: plans/check-in-create-form.md
 *
 * Mobile-first; sticky bottom save bar with safe-area-inset.
 * Save flow is best-effort: session POST first, then per-draft-job POSTs.
 * Failures are passed to the session detail page via sessionStorage so the
 * detail page can offer a retry banner.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { IconArrowLeft, IconDeviceTv, IconPlus } from '@tabler/icons-react';
import type {
  CheckInChargeLine,
  CheckInDeviceType,
  CheckInDraftJobCreate,
  CheckInIntakeType,
  CheckInSessionCreate,
  WallPlaylist,
  WorkItem,
} from '@techgitter/contracts';

import '../styles/checkInCreate.css';
import { CARD_SX, PRIMARY_BTN_SX, OUTLINED_BTN_SX } from '../styles/designSystem';
import { FormField } from '../components/panel/FormField';
import { RadioOption } from '../components/panel/RadioOption';
import { Chip } from '../components/chip';
import {
  addCheckInDraftJob,
  createCheckInSession,
} from '../services/checkInApi';
import {
  fetchWallPlaylists,
  setWallPlaylist,
  startWallCheckIn,
} from '../services/wallAdminApi';
import { CustomerLookupAutocomplete } from '../components/check-in/CustomerLookupAutocomplete';
import { splitFullName, type CustomerNameParts } from '../components/check-in/customerNameParts';
import { JobPicker } from '../components/check-in/JobPicker';
import {
  DraftJobAccordionRow,
  createDraftJobFormState as newDraftJob,
  type DraftJobFormState,
} from '../components/check-in/DraftJobAccordionRow';

/** Recessive grey Cancel button (skill §14.13 — never blue/bordered). */
const CANCEL_BTN_SX = { color: '#888', '&:hover': { color: '#555', bgcolor: 'transparent' } };

export interface CheckInCreatePageProps {
  onBack: () => void;
  onCreated: (sessionId: string) => void;
  /**
   * Pre-seed for the "Create Return" flow. When provided, the page locks
   * intakeType to 'in_store_return', pre-fills the customer + service summary
   * from this work item, and skips the in-page job picker.
   */
  returnOfWorkItem?: WorkItem | null;
  /**
   * Pre-seed for a Drop Off against an open job — the customer is bringing
   * back a device that was released to them while the job waited for parts
   * (a scan of its label lands here via `/check-in/new?dropoff={n}`). Locks
   * intakeType to 'in_store_dropoff', otherwise exactly like
   * `returnOfWorkItem`.
   */
  dropOffForWorkItem?: WorkItem | null;
}

type DeliveryMethod = 'email' | 'sms' | 'both';

const PHONE_AU_PREFIX_RE = /^04\d{8}$/;

/**
 * Intake types that link the new session to an existing job (Return links to a
 * closed job; Drop-Off links to an open, in-progress job). Both require an
 * `originalWorkItem` and are single-device-per-session by convention.
 */
const linksToJob = (t: CheckInIntakeType | null): boolean =>
  t === 'in_store_return' || t === 'in_store_dropoff';

export const CheckInCreatePage: React.FC<CheckInCreatePageProps> = ({
  onBack,
  onCreated,
  returnOfWorkItem,
  dropOffForWorkItem,
}) => {
  // The job a deep link has already chosen, for either linked intake type.
  const seedWorkItem = returnOfWorkItem ?? dropOffForWorkItem ?? null;
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));
  // Below `sm` the Start-on-wall card stacks (see its sx); the playlist select then
  // spans the row beside the button instead of stopping at 170px.
  const isStacked = useMediaQuery(theme.breakpoints.down('sm'));

  // Session-level state.
  //
  // `null` until reception picks a tile — there is deliberately no default.
  // In-store used to be preselected, and splitting it into Single job / Multi
  // jobs is exactly where a default stops being harmless: the grouping decides
  // whether the check-in becomes one RepairShopr ticket or N, and by the time
  // the tickets exist nobody can see what was chosen. The one exception is the
  // "Create Return" deep link, which arrives having already made the choice.
  const [intakeType, setIntakeType] = useState<CheckInIntakeType | null>(
    returnOfWorkItem ? 'in_store_return' : dropOffForWorkItem ? 'in_store_dropoff' : null,
  );
  // One RS ticket for all the devices, or one each. Only meaningful for the
  // in-store tiles, which are the only ones that offer the choice.
  const [ticketGrouping, setTicketGrouping] = useState<CheckInTicketGrouping | null>(
    seedWorkItem ? 'single_job' : null,
  );
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('email');
  const [customerFirstName, setCustomerFirstName] = useState('');
  const [customerLastName, setCustomerLastName] = useState('');
  // Both prefill sources (the lookup index, and the WorkItem behind a
  // return/drop-off) offer one joined name, so they set the pair together.
  const setName = useCallback((parts: CustomerNameParts) => {
    setCustomerFirstName(parts.customerFirstName);
    setCustomerLastName(parts.customerLastName);
  }, []);
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [businessName, setBusinessName] = useState('');
  // Customer designation of the most recently picked customer from the
  // lookup — raw wire value ("Core" | "Core+" | other | null). Drives
  // default diagnostic-fee suppression in the prelim charges editor:
  // Core and Core+ customers are billed via monthly statement so we
  // don't auto-charge a diag fee at check-in. Other tiers (and
  // un-designated customers) pay the standard diag. The receptionist
  // can still opt in per-device with the "Apply diagnostic fee" button.
  const [customerDesignation, setCustomerDesignation] = useState<string | null>(null);
  // RepairShopr customer id captured when the receptionist picks a
  // customer from the lookup. Sent in the create payload so the session
  // row has a matched RS customer immediately — the "link existing RS
  // ticket" picker on the session-detail page needs this to work.
  const [pickedRepairShoprCustomerId, setPickedRepairShoprCustomerId] = useState<string | null>(null);
  const [customerNotes, setCustomerNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  // The existing job this session is linked to (Return → a closed job;
  // Drop-Off → an open, in-progress job). Either seeded by the "Create Return"
  // button on a job page, or picked in-page via the JobPicker after selecting
  // the "In-store (Return)" / "In-store (Drop Off)" radio.
  const [originalWorkItem, setOriginalWorkItem] = useState<WorkItem | null>(seedWorkItem);

  // Draft jobs
  const [draftJobs, setDraftJobs] = useState<DraftJobFormState[]>([newDraftJob()]);
  const [expandedJobIndex, setExpandedJobIndex] = useState<number | null>(0);

  // Submission state
  const [isSaving, setIsSaving] = useState(false);
  const [isStartingOnWall, setIsStartingOnWall] = useState(false);
  const [isSwitchingWall, setIsSwitchingWall] = useState(false);
  const [wallPlaylists, setWallPlaylists] = useState<WallPlaylist[]>([]);
  // '' = let the wall choose automatically (customer-driven, then default).
  const [wallPlaylistId, setWallPlaylistId] = useState('');
  const [topLevelError, setTopLevelError] = useState<string | null>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const isDirty = useMemo(
    () =>
      !!customerFirstName ||
      !!customerLastName ||
      !!customerEmail ||
      !!customerPhone ||
      !!businessName ||
      !!customerNotes ||
      !!internalNotes ||
      draftJobs.some(j => j.serviceSummary || j.requirements || j.customerVisibleDetails || j.internalNotes),
    [customerFirstName, customerLastName, customerEmail, customerPhone, businessName, customerNotes, internalNotes, draftJobs],
  );

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setCancelConfirmOpen(true);
    } else {
      onBack();
    }
  }, [isDirty, onBack]);

  const applyPrefillFromOriginal = useCallback((w: WorkItem) => {
    // A WorkItem carries only a joined name, so it is split for the two
    // fields; reception can correct the guess before saving.
    setName(splitFullName(w.customerName));
    setCustomerEmail(w.customerEmail ?? '');
    setCustomerPhone(w.customerMobile || w.customerPhone || '');
    setBusinessName(w.customerBusinessName ?? '');
    const numLabel = w.number != null ? `#${w.number}` : `#${w.id}`;
    const verb = intakeType === 'in_store_dropoff' ? 'Drop-off for' : 'Return of';
    const summary = `${verb} ${numLabel}${w.title ? ` — ${w.title}` : ''}`;
    const deviceType = mapProblemTypeToDeviceType(w.problemType);
    setDraftJobs(prev => {
      // Returns and drop-offs are single-device by design — collapse to one row
      // if multiples were created before the user flipped intake type.
      const base = prev[0] ?? newDraftJob();
      return [{
        ...base,
        deviceType: deviceType ?? base.deviceType,
        serviceSummary: summary,
      }];
    });
    setExpandedJobIndex(0);
  }, [intakeType, setName]);

  // Seed once on mount if the parent handed us an original work item (deep
  // link from the "Create Return" button on the job page).
  useEffect(() => {
    if (seedWorkItem) {
      applyPrefillFromOriginal(seedWorkItem);
    }
    // Run once for the initial deep-link only; subsequent in-page picker
    // selections route through handleReturnPick directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLinkedJobPick = useCallback((w: WorkItem) => {
    setOriginalWorkItem(w);
    applyPrefillFromOriginal(w);
  }, [applyPrefillFromOriginal]);

  const validate = (): string | null => {
    if (!intakeType || !ticketGrouping) return 'Pick a check-in type.';
    if (intakeType === 'online' && !deliveryMethod) return 'Pick a delivery method.';
    if (intakeType === 'express') {
      if (!customerFirstName.trim() || !customerLastName.trim()) {
        return 'Express check-ins need a customer first and last name.';
      }
      if (!customerPhone.trim()) return 'Express check-ins need a customer phone number.';
    }
    if (intakeType === 'in_store_return' && !originalWorkItem) {
      return 'Pick the original job before saving the return.';
    }
    if (intakeType === 'in_store_dropoff' && !originalWorkItem) {
      return 'Pick the in-progress job before saving the drop-off.';
    }
    if (draftJobs.length === 0) return 'Add at least one device.';
    for (let i = 0; i < draftJobs.length; i++) {
      const j = draftJobs[i];
      // The device type is never inherited, so this applies to every device.
      if (j.deviceType === 'other' && !j.deviceTypeOther.trim()) {
        return `Device ${i + 1} is "Other" — say what it is.`;
      }
      if (inheritsJobFields(i)) continue;
      if (!j.serviceSummary.trim()) return `Device ${i + 1} needs a Service Summary.`;
    }
    return null;
  };

  const handleSave = async () => {
    setTopLevelError(null);
    const validationError = validate();
    if (validationError) {
      setTopLevelError(validationError);
      return;
    }

    setIsSaving(true);

    const sessionPayload: CheckInSessionCreate = {
      // `validate()` has already refused a null pair.
      intakeType: intakeType as CheckInIntakeType,
      ticketGrouping: ticketGrouping as CheckInTicketGrouping,
      ...(intakeType === 'online' ? { deliveryMethod } : {}),
      ...(linksToJob(intakeType) && originalWorkItem
        ? { originalWorkItemId: originalWorkItem.id }
        : {}),
      customerFirstName: customerFirstName.trim() || undefined,
      customerLastName: customerLastName.trim() || undefined,
      customerEmail: normalizeEmail(customerEmail),
      customerPhone: normalizePhone(customerPhone),
      businessName: businessName.trim() || undefined,
      customerNotes: customerNotes.trim() || undefined,
      internalNotes: internalNotes.trim() || undefined,
      ...(customerDesignation
        ? { customerDesignation }
        : {}),
      ...(pickedRepairShoprCustomerId
        ? { repairShoprCustomerId: pickedRepairShoprCustomerId }
        : {}),
    };

    let sessionId: string;
    try {
      const created = await createCheckInSession(sessionPayload);
      sessionId = created.id;
    } catch (err) {
      setIsSaving(false);
      setTopLevelError(err instanceof Error ? err.message : 'Failed to create session');
      return;
    }

    // Best-effort per-draft-job POSTs. Collect failures, redirect with retry context.
    const failedDraftJobs: { clientId: string; payload: CheckInDraftJobCreate; error: string }[] = [];
    for (const [jobIndex, job] of draftJobs.entries()) {
      const payload: CheckInDraftJobCreate = {
        deviceType: job.deviceType,
        ...(job.deviceType === 'other' ? { deviceTypeOther: job.deviceTypeOther.trim() } : {}),
        // Omitted when inherited, so the server copies device 1's rather than
        // this page overwriting them with its own form defaults. The devices
        // are POSTed in order, so device 1 exists by the time device 2 is sent.
        ...(inheritsJobFields(jobIndex)
          ? {}
          : {
            serviceMode: job.serviceMode,
            serviceSummary: job.serviceSummary.trim(),
          }),
        requirements: job.requirements.trim() || undefined,
        customerVisibleDetails: job.customerVisibleDetails.trim() || undefined,
        internalNotes: job.internalNotes.trim() || undefined,
        // Express sessions never carry prelim charges — money is collected
        // on the monthly statement. The editor is hidden on Express, but
        // the auto-compose effect may have populated charges before the
        // receptionist flipped intake-type to Express, so strip the list
        // here so we don't persist a stale $90 diagnostic line.
        //
        // An inheriting device is stripped for the same reason and a different
        // one: the job is diagnosed once, so a second diagnostic fee would be
        // charging twice for one job. The flip case is the one that needs the
        // strip — charges auto-composed while the tile said Multi jobs.
        preliminaryCharges:
          intakeType === 'express' || inheritsJobFields(jobIndex)
            ? []
            : job.preliminaryCharges,
      };
      try {
        await addCheckInDraftJob(sessionId, payload);
      } catch (err) {
        failedDraftJobs.push({
          clientId: job.clientId,
          payload,
          error: err instanceof Error ? err.message : 'Failed to add device',
        });
      }
    }

    if (failedDraftJobs.length > 0) {
      const retryKey = `sm4rt:checkin:retry:${sessionId}`;
      try {
        sessionStorage.setItem(retryKey, JSON.stringify(failedDraftJobs));
      } catch {
        /* sessionStorage full / disabled — fall back to query string only */
      }
    }

    setIsSaving(false);
    onCreated(sessionId);
  };

  const updateJob = (clientId: string, patch: Partial<DraftJobFormState>) => {
    setDraftJobs(prev => prev.map(j => (j.clientId === clientId ? { ...j, ...patch } : j)));
  };

  const updateJobCharges = (clientId: string, charges: CheckInChargeLine[]) => {
    setDraftJobs(prev => prev.map(j => (j.clientId === clientId ? { ...j, preliminaryCharges: charges } : j)));
  };

  /**
   * In single-job grouping the service decisions describe the one job, not the
   * one device: device 1 captures the summary, tier, mode, urgency and charges,
   * and every later device inherits them. The summary and service mode are
   * filled in by `add_draft_job` server-side when omitted.
   *
   * This has to be here as well as in `AddDeviceDialog` — the create page is
   * where a two-device check-in is normally built, and without it device 2
   * showed a required field the server was about to fill anyway, so the save
   * was refused with "Device 2 needs a Service Summary".
   */
  const inheritsJobFields = (index: number): boolean =>
    ticketGrouping === 'single_job' && index > 0;

  const addJob = () => {
    setDraftJobs(prev => [...prev, newDraftJob()]);
    // Phones: collapse all others, expand new one. Desktop: same (simpler).
    setExpandedJobIndex(draftJobs.length);
  };

  const removeJob = (clientId: string) => {
    setDraftJobs(prev => prev.filter(j => j.clientId !== clientId));
    setExpandedJobIndex(null);
  };

  const handleJobToggle = (index: number) => {
    if (isPhone) {
      setExpandedJobIndex(prev => (prev === index ? null : index));
    } else {
      setExpandedJobIndex(prev => (prev === index ? null : index));
    }
  };

  /**
   * The tiles. The two in-store ones both submit `intakeType: 'in_store'` and
   * differ only in the grouping — which is why they are keyed by a composite
   * value here rather than by intake type.
   *
   * The grouping is NOT an intake type on the wire: `intakeType` is
   * load-bearing for sm4rt-public-api's terms path, where the allow-list is
   * exact and the template lookup has no family fallback, so an
   * `in_store_single` session could not accept terms at all.
   */
  const handleIntakeChange = (next: string) => {
    if (next === 'in_store:single_job' || next === 'in_store:job_per_device') {
      setIntakeType('in_store');
      setTicketGrouping(
        next === 'in_store:single_job' ? 'single_job' : 'job_per_device',
      );
      setOriginalWorkItem(null);
      return;
    }
    const nextType = next as CheckInIntakeType;
    setIntakeType(nextType);
    // Every other tile is one ticket per device: express and online have no
    // grouping choice, and a drop-off's devices all inherit the one ticket of
    // the job they extend, which is single-job arrived at from the other side.
    setTicketGrouping(linksToJob(nextType) ? 'single_job' : 'job_per_device');
    // Clear the locked-in linked job when leaving a job-linking type (Return /
    // Drop-Off) — keeps the payload coherent.
    if (!linksToJob(nextType)) setOriginalWorkItem(null);
  };

  /**
   * Start the check-in on the reception wall instead of typing the customer's
   * details here.
   *
   * Creates the same in-store draft this form would, stamps it as the current
   * wall QR, and drops straight into the session detail — so the customer
   * fills in their contact and accepts the terms on their own phone while
   * staff get on with the devices. Any QR already on the wall is superseded.
   */
  // Best-effort: no playlists (or none provisioned) simply means no picker and
  // the wall keeps choosing automatically.
  useEffect(() => {
    const controller = new AbortController();
    fetchWallPlaylists(controller.signal)
      .then(setWallPlaylists)
      .catch(() => setWallPlaylists([]));
    return () => controller.abort();
  }, []);

  /**
   * Switch the wall without starting a check-in — the customer who is only
   * browsing. The server ends it on a timeout, since there is no check-in
   * whose completion could.
   */
  const handleSwitchWallPlaylist = useCallback(async () => {
    if (!wallPlaylistId) return;
    setIsSwitchingWall(true);
    setTopLevelError(null);
    try {
      await setWallPlaylist({ playlistId: wallPlaylistId });
    } catch (err) {
      setTopLevelError(
        err instanceof Error ? err.message : 'Could not switch the wall playlist.',
      );
    } finally {
      setIsSwitchingWall(false);
    }
  }, [wallPlaylistId]);

  const handleStartOnWall = useCallback(async () => {
    setIsStartingOnWall(true);
    setTopLevelError(null);
    try {
      // The playlist rides the same call — one decision, one request.
      const session = await startWallCheckIn({
        playlistId: wallPlaylistId || undefined,
        // Non-null: the card this runs from is only offered once an in-store
        // tile has been picked.
        ticketGrouping: ticketGrouping ?? undefined,
      });
      onCreated(session.id);
    } catch (err) {
      setTopLevelError(
        err instanceof Error
          ? err.message
          : 'Could not put a check-in on the wall. Please try again.',
      );
      setIsStartingOnWall(false);
    }
  }, [onCreated, wallPlaylistId, ticketGrouping]);

  return (
    <div className="checkin-create">
      {/* Sticky header */}
      <div className="checkin-create__topbar">
        <button
          type="button"
          className="checkin-create__back"
          onClick={handleCancel}
          aria-label="Back to dashboard"
        >
          <IconArrowLeft size={18} stroke={1.5} />
        </button>
        <h1 className="checkin-create__title">New Check-In</h1>
        {!isPhone && (
          <div className="checkin-create__topbar-actions">
            <Button variant="text" onClick={handleCancel} disabled={isSaving} sx={CANCEL_BTN_SX}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={isSaving} sx={PRIMARY_BTN_SX}>
              {isSaving ? 'Saving…' : 'Save Check-In'}
            </Button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="checkin-create__content">
        <div className="checkin-create__inner">
          {topLevelError && (
            <Alert severity="error" onClose={() => setTopLevelError(null)}>
              {topLevelError}
            </Alert>
          )}

          {/*
            The wall route. Offered only for a plain in-store check-in — the
            wall is a screen in reception, so a remote/online session or a
            return that follows a specific work item has nothing to put on it.
            Hidden once the form is dirty, so it can't silently discard details
            someone has already typed.

            It also waits for a grouping. The wall creates the session before
            any device exists, so this is the last moment the choice can be
            made, and it is not one to default on someone's behalf.
          */}
          {intakeType === 'in_store' && ticketGrouping && !seedWorkItem && !isDirty && (
            /* One row on a wide screen; stacked below 600px (MUI `sm`). As a row on a
               phone the controls (~320px, non-shrinking) left the text a column one
               word wide. */
            <Box
              sx={{
                ...CARD_SX,
                p: 2,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="checkin-create__card-title">Customer is at the counter?</div>
                <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>
                  Put a QR on the reception TV and let them enter their own details
                  while you check the devices in.
                  {' '}
                  {ticketGrouping === 'single_job'
                    ? 'One ticket for everything they bring in.'
                    : 'One ticket per device.'}
                </div>
              </div>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
                {wallPlaylists.length > 0 && (
                  <select
                    aria-label="Wall playlist"
                    value={wallPlaylistId}
                    onChange={(e) => setWallPlaylistId(e.target.value)}
                    disabled={isStartingOnWall || isSwitchingWall}
                    style={{
                      height: 36,
                      padding: '0 8px',
                      fontSize: 14,
                      color: '#1a1a1a',
                      border: '0.5px solid #d0dcea',
                      borderRadius: 6,
                      background: '#fff',
                      // Stacked (phone): take the row's slack beside the button.
                      flex: '1 1 auto',
                      minWidth: 0,
                      maxWidth: isStacked ? undefined : 170,
                    }}
                  >
                    {/* The default. The wall already picks Business or
                        Residential from what the customer types — overriding
                        should be the deliberate choice, not the starting one. */}
                    <option value="">Choose automatically</option>
                    {wallPlaylists.map((playlist) => (
                      <option key={playlist.id} value={playlist.id}>
                        {playlist.name}
                      </option>
                    ))}
                  </select>
                )}
                <Button
                  variant="outlined"
                  onClick={handleStartOnWall}
                  disabled={isStartingOnWall || isSaving}
                  startIcon={<IconDeviceTv size={16} stroke={1.5} />}
                  sx={{ ...OUTLINED_BTN_SX, flexShrink: 0 }}
                >
                  {isStartingOnWall ? 'Starting…' : 'Start on wall'}
                </Button>
              </Box>
            </Box>
          )}

          {/*
            The browsing customer: change what's on the wall without starting a
            check-in. Only offered once a specific playlist is chosen —
            "Choose automatically" is what the wall already does, so there
            would be nothing to switch to.
          */}
          {intakeType === 'in_store' && ticketGrouping && !seedWorkItem && !isDirty &&
            wallPlaylists.length > 0 && wallPlaylistId && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="text"
                onClick={handleSwitchWallPlaylist}
                disabled={isSwitchingWall || isStartingOnWall}
                sx={{ color: '#4A90D9' }}
              >
                {isSwitchingWall
                  ? 'Switching…'
                  : 'Just switch the wall (no check-in)'}
              </Button>
            </Box>
          )}

          {/* Check-In Type */}
          <Box sx={{ ...CARD_SX, p: 2 }}>
            <div className="checkin-create__card-title">Check-In Type</div>
            <div className="radio-option-group">
              {/*
                Two in-store tiles, one grouping each. The helper text names
                the trade because this is the only moment reception can choose
                it and the consequence is invisible afterwards — a technician
                looking at one ticket for three laptops cannot tell whether
                that was decided or defaulted.
              */}
              <RadioOption
                name="intakeType"
                value="in_store:single_job"
                checked={intakeType === 'in_store' && ticketGrouping === 'single_job'}
                onChange={handleIntakeChange}
                label="In-store (Single job)"
                description="One ticket, one status, one invoice for everything checked in."
              />
              <RadioOption
                name="intakeType"
                value="in_store:job_per_device"
                checked={intakeType === 'in_store' && ticketGrouping === 'job_per_device'}
                onChange={handleIntakeChange}
                label="In-store (Multi jobs)"
                description="Each device tracked and billed on its own ticket."
              />
              <RadioOption name="intakeType" value="online" checked={intakeType === 'online'} onChange={handleIntakeChange} label="Online" />
              <RadioOption
                name="intakeType"
                value="express"
                checked={intakeType === 'express'}
                onChange={handleIntakeChange}
                label="Express"
                description="Business customer on a monthly invoice agreement. No customer-facing intake, no money to collect. Prints a check-in slip immediately."
              />
              <RadioOption
                name="intakeType"
                value="in_store_return"
                checked={intakeType === 'in_store_return'}
                onChange={handleIntakeChange}
                label="In-store (Return)"
                description="Customer brings back a device from a previously closed job. Pick the original ticket to pre-fill customer and device details."
              />
              <RadioOption
                name="intakeType"
                value="in_store_dropoff"
                checked={intakeType === 'in_store_dropoff'}
                onChange={handleIntakeChange}
                label="In-store (Drop Off)"
                description="Customer dropping off items for completion of in-progress job"
              />
            </div>
          </Box>

          {/* Linked Job (Return / Drop-Off) */}
          {linksToJob(intakeType) && (
            <Box sx={{ ...CARD_SX, p: 2 }}>
              <div className="checkin-create__card-title">
                {intakeType === 'in_store_dropoff' ? 'In-Progress Job' : 'Original Job'}
              </div>
              {originalWorkItem ? (
                <div className="checkin-create__return-row">
                  <Chip tone="info">
                    {(() => {
                      const verb = intakeType === 'in_store_dropoff' ? 'Drop-off for' : 'Return of';
                      const ref = originalWorkItem.number != null
                        ? `#${originalWorkItem.number}${originalWorkItem.title ? ` — ${originalWorkItem.title}` : ''}`
                        : `#${originalWorkItem.id}`;
                      return `${verb} ${ref}`;
                    })()}
                  </Chip>
                  <Button variant="text" size="small" onClick={() => setOriginalWorkItem(null)} sx={{ color: '#4A90D9' }}>
                    Change
                  </Button>
                </div>
              ) : (
                <JobPicker
                  scope={intakeType === 'in_store_dropoff' ? 'open' : 'history'}
                  onPick={handleLinkedJobPick}
                />
              )}
              <p className="checkin-create__hint">
                {intakeType === 'in_store_dropoff'
                  ? 'Customer and device details pre-fill from the in-progress job but remain editable. Confirm device serial / manufacturer with the customer — those aren’t auto-filled.'
                  : 'Customer and device details pre-fill from the original job but remain editable. Confirm device serial / manufacturer with the customer — those aren’t auto-filled.'}
              </p>
            </Box>
          )}

          {/* Customer Details */}
          <Box sx={{ ...CARD_SX, p: 2 }}>
            <div className="checkin-create__card-title">
              Customer Details
              {customerDesignation && (
                <Chip tone={isCoreDesignation(customerDesignation) ? 'success' : 'neutral'}>
                  Designation: {customerDesignation}
                </Chip>
              )}
            </div>
            <div className="checkin-create__field-col">
              <CustomerLookupAutocomplete
                defaultBusinessOnly={intakeType === 'express'}
                onPick={c => {
                  // The lookup index carries only a joined name; split it
                  // into the two fields reception can then correct.
                  setName(splitFullName(c.fullName || c.displayName || ''));
                  setCustomerEmail(c.email ?? '');
                  setCustomerPhone(c.mobile ?? c.phone ?? '');
                  setBusinessName(c.businessName ?? '');
                  setCustomerDesignation(c.designation ?? null);
                  setPickedRepairShoprCustomerId(c.repairShoprCustomerId || null);
                }}
              />
              <FormField label="First Name">
                <input
                  value={customerFirstName}
                  onChange={e => setCustomerFirstName(e.target.value)}
                  autoComplete="given-name"
                />
              </FormField>
              <FormField label="Last Name">
                <input
                  value={customerLastName}
                  onChange={e => setCustomerLastName(e.target.value)}
                  autoComplete="family-name"
                />
              </FormField>
              <FormField label="Email">
                <input
                  value={customerEmail}
                  onChange={e => setCustomerEmail(e.target.value)}
                  onBlur={() => setCustomerEmail(normalizeEmail(customerEmail) ?? '')}
                  inputMode="email"
                  autoComplete="email"
                />
              </FormField>
              <FormField label="Phone">
                <input
                  value={customerPhone}
                  onChange={e => setCustomerPhone(e.target.value)}
                  onBlur={() => setCustomerPhone(normalizePhone(customerPhone) ?? '')}
                  inputMode="tel"
                  autoComplete="tel"
                />
              </FormField>
              <FormField label="Business Name">
                <input value={businessName} onChange={e => setBusinessName(e.target.value)} autoComplete="organization" />
              </FormField>
              <FormField label="Customer Notes (customer-visible)">
                <textarea rows={2} value={customerNotes} onChange={e => setCustomerNotes(e.target.value)} />
              </FormField>
            </div>
          </Box>

          {/* Delivery Method (online only) */}
          {intakeType === 'online' && (
            <Box sx={{ ...CARD_SX, p: 2 }}>
              <div className="checkin-create__card-title">Delivery Method</div>
              <div className="radio-option-group radio-option-group--row">
                <RadioOption name="deliveryMethod" value="email" checked={deliveryMethod === 'email'} onChange={v => setDeliveryMethod(v as DeliveryMethod)} label="Email" />
                <RadioOption name="deliveryMethod" value="sms" checked={deliveryMethod === 'sms'} onChange={v => setDeliveryMethod(v as DeliveryMethod)} label="SMS" />
                <RadioOption name="deliveryMethod" value="both" checked={deliveryMethod === 'both'} onChange={v => setDeliveryMethod(v as DeliveryMethod)} label="Both" />
              </div>
            </Box>
          )}

          {/* Devices */}
          <Box sx={{ ...CARD_SX, p: 2 }}>
            <div className="checkin-create__card-title">Devices ({draftJobs.length})</div>
            <div className="checkin-create__devices">
              {draftJobs.map((job, idx) => (
                <DraftJobAccordionRow
                  key={job.clientId}
                  index={idx}
                  job={job}
                  expanded={expandedJobIndex === idx}
                  showRemove={draftJobs.length > 1}
                  isExpress={intakeType === 'express'}
                  intakeType={intakeType}
                  originalWorkItemId={originalWorkItem?.id ?? null}
                  isCoreCustomer={isCoreDesignation(customerDesignation)}
                  inheritsJobFields={inheritsJobFields(idx)}
                  onToggle={() => handleJobToggle(idx)}
                  onChange={patch => updateJob(job.clientId, patch)}
                  onChargesChange={charges => updateJobCharges(job.clientId, charges)}
                  onRemove={() => removeJob(job.clientId)}
                />
              ))}
            </div>
            {!linksToJob(intakeType) && (
              <Button
                fullWidth={isPhone}
                startIcon={<IconPlus size={16} stroke={1.5} />}
                onClick={addJob}
                sx={{ ...OUTLINED_BTN_SX, mt: 1.5 }}
                variant="outlined"
              >
                Add device
              </Button>
            )}
          </Box>

          {/* Internal Session Notes */}
          <Box sx={{ ...CARD_SX, p: 2 }}>
            <div className="checkin-create__card-title">Internal Session Notes</div>
            <FormField label="Staff-only notes">
              <textarea
                rows={2}
                value={internalNotes}
                onChange={e => setInternalNotes(e.target.value)}
                placeholder="Staff-only notes about this session"
              />
            </FormField>
          </Box>
        </div>
      </div>

      {/* Sticky bottom action bar — phone only */}
      {isPhone && (
        <div className="checkin-create__bottombar">
          <Button fullWidth variant="text" onClick={handleCancel} disabled={isSaving} sx={CANCEL_BTN_SX}>
            Cancel
          </Button>
          <Button fullWidth variant="contained" onClick={handleSave} disabled={isSaving} sx={PRIMARY_BTN_SX}>
            {isSaving ? 'Saving…' : 'Save Check-In'}
          </Button>
        </div>
      )}

      <Dialog open={cancelConfirmOpen} onClose={() => setCancelConfirmOpen(false)}>
        <DialogTitle>Discard this draft Check-In?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You'll lose the customer details and devices you've entered.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)} sx={CANCEL_BTN_SX}>Keep editing</Button>
          <Button
            onClick={() => {
              setCancelConfirmOpen(false);
              onBack();
            }}
            color="error"
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// Accept every canonical wire form for the Core / Core+ designation.
// Dataverse may store either "Core+" or "CorePlus" depending on which
// import path created the customer; the customerContextMapper normalises
// to "CorePlus" but the lookup index ships the raw stored value, so the
// frontend must tolerate both.
function isCoreDesignation(designation: string | null): boolean {
  if (!designation) return false;
  const normalized = designation.trim().toLowerCase();
  return (
    normalized === 'core' ||
    normalized === 'core+' ||
    normalized === 'coreplus'
  );
}

function normalizeEmail(value: string): string | undefined {
  const trimmed = value.trim().toLowerCase();
  return trimmed ? trimmed : undefined;
}

function normalizePhone(value: string): string | undefined {
  if (!value) return undefined;
  const cleaned = value.replace(/[^\d+]/g, '');
  if (PHONE_AU_PREFIX_RE.test(cleaned)) {
    return `+61${cleaned.slice(1)}`;
  }
  return cleaned || undefined;
}

function mapProblemTypeToDeviceType(problemType: string | undefined | null): CheckInDeviceType | null {
  if (!problemType) return null;
  const normalized = problemType.trim().toLowerCase();
  if (normalized.includes('all-in-one') || normalized.includes('all in one')) return 'all_in_one';
  if (normalized.includes('laptop') || normalized.includes('notebook')) return 'laptop';
  if (normalized.includes('desktop') || normalized.includes('tower') || normalized.includes('pc')) return 'desktop';
  if (normalized.includes('tablet') || normalized.includes('ipad')) return 'tablet';
  if (normalized.includes('phone')) return 'phone';
  if (normalized.includes('usb') || normalized.includes('flash drive') || normalized.includes('thumb drive')) return 'usb_flash_drive';
  if (normalized.includes('hard drive') || normalized.includes('hdd') || normalized.includes('ssd') || normalized.includes('hard disk')) return 'hard_drive';
  if (
    normalized.includes('console') || normalized.includes('playstation') ||
    normalized.includes('xbox') || normalized.includes('nintendo') ||
    normalized.includes('switch') || normalized.includes('steam deck')
  ) return 'console';
  return null;
}
